import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
// Store
import { IRootState } from '../../../../redux';
import { changeLanguage } from '../../../../redux/user/actions';
// Interfaces
import { ILanguage } from '../../../../interfaces/globalization.interfaces';
// Styles
import styles from './footertranslate.module.scss';
// Config
import { ConfigContext } from '../../../../providers';
// Helpers
import { getCurrentLanguage, closePopupOFocusOut } from '../../../../helpers/general';
import { isTouchScreen } from '../../../../helpers/browser';
import KeyboardHandler from '../../../../helpers/keyboardHandler';
import { setSupportedLanguages } from '../../../../redux/appreadouts/actions';

const listItemHeight = 36;

interface IFooterTranslateState {
    isOpened: boolean;
    cursor: number;
}

interface IFooterTranslateProps {
    color?: string;
}

class FooterTranslate extends React.Component<ReduxType, IFooterTranslateState> {
    keyboardHandlerInstance = new KeyboardHandler();
    listRef = React.createRef<HTMLUListElement>();

    constructor(props: ReduxType) {
        super(props);

        this.state = {
            isOpened: false,
            cursor: 0,
        };
    }

    componentDidMount(): void {
        this.setupKeyboardHandler();
        this.setState({ cursor: this.getSelectedLanguageIndex() });
        window.addEventListener('click', this.hideIfShown);
    }

    componentWillUnmount(): void {
        window.removeEventListener('click', this.hideIfShown);
    }

    setupKeyboardHandler = (): void => {
        this.keyboardHandlerInstance.addHandlers({
            enter: this.onEnterKeyHandler,
            esc: () => this.setState({ isOpened: false }),
            space: this.toggle,
        });
    };

    getSelectedLanguageIndex = (): number => {
        const { supportedLanguages, languageId } = this.props;
        if (supportedLanguages && supportedLanguages.length) {
            const langIndex = supportedLanguages.findIndex((lang) => languageId === lang.id);
            return langIndex < 0 ? 0 : langIndex;
        } else {
            return 0;
        }
    };

    onMouseEnterHandler = (index: number): void => {
        if (index !== this.state.cursor) {
            this.setState({ cursor: index });
        }
    };

    onMouseLeaveHandler = (): void => {
        this.setState({ cursor: this.getSelectedLanguageIndex() });
    };

    onEnterKeyHandler = (event: React.KeyboardEvent): void => {
        this.toggle(event);
    };

    scrollList = (): void => {
        const scrollTopNeeded = this.listRef.current.scrollTop > this.state.cursor * listItemHeight;
        const scrollBottomNeeded =
            this.listRef.current.scrollTop + this.listRef.current.clientHeight <
            (this.state.cursor + 1) * listItemHeight;
        if (scrollTopNeeded || scrollBottomNeeded) {
            this.listRef.current.scrollTop = this.state.cursor * listItemHeight;
        }
    };

    changeLanguage = (id: number): void => {
        this.props.changeLanguage(id);
    };

    hideIfShown = (): void => {
        if (!this.state.isOpened) return;
        this.setState((state) => ({ isOpened: !state.isOpened }));
    };

    toggle = (event: React.MouseEvent | React.KeyboardEvent): void => {
        event.stopPropagation();
        this.setState((state) => ({
            isOpened: !state.isOpened,
            cursor: this.getSelectedLanguageIndex(),
        }));
    };

    public render(): JSX.Element {
        const chosenLanguage = getCurrentLanguage(this.props.supportedLanguages, this.props.languageId);
        const notTouchScreen = !isTouchScreen();

        const role = '';

        return (
            <ConfigContext.Consumer>
                {(config): JSX.Element => (
                    <div
                        tabIndex={0}
                        role="button"
                        // className={styles.translateBox}
                        className={[styles.translateBox, 'prevFocusedElem'].join(' ')}
                        onClick={this.toggle}
                        {...(notTouchScreen && { onKeyDown: this.keyboardHandlerInstance.onPress })}
                        onBlur={(e) => {
                            closePopupOFocusOut(e, false) && this.setState(() => ({ isOpened: false }));
                        }}
                    >
                        <span>
                            <FormattedMessage id="LANGUAGE_CHANGE_TITLE" />
                        </span>
                        <span className={styles.mobileLable}>{chosenLanguage.localName}</span>
                        <div className={styles.dropdown}>
                            <span className={styles.desktopLable}>{chosenLanguage.localName}</span>
                            <ul
                                className={styles.optionsList}
                                hidden={!this.state.isOpened}
                                style={{
                                    backgroundColor: this.props.color
                                        ? this.props.color
                                        : config.colors.header.background,
                                }}
                                ref={this.listRef}
                            >
                                {this.props.supportedLanguages.map((lang: ILanguage, index: number) => (
                                    <li
                                        key={lang.id}
                                        tabIndex={0}
                                        role={role}
                                        onClick={() => this.changeLanguage(lang.id)}
                                        onKeyDown={(e) => e.key == 'Enter' && this.changeLanguage(lang.id)}
                                        className={
                                            this.state.cursor === index
                                                ? [styles.active, 'prevFocusedElem'].join(' ')
                                                : 'prevFocusedElem'
                                        }
                                        style={{ height: listItemHeight }}
                                        {...(notTouchScreen && {
                                            onMouseEnter: () => this.onMouseEnterHandler(index),
                                            onMouseLeave: this.onMouseLeaveHandler,
                                        })}
                                    >
                                        {lang.localName}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </ConfigContext.Consumer>
        );
    }
}

const mapDispatcherToProps = (dispatch: Dispatch) => {
    return {
        changeLanguage: (languageId: number) => dispatch(changeLanguage(languageId)),
        setSupportedLanguages: (languages: ILanguage[]) => dispatch(setSupportedLanguages(languages)),
    };
};

const mapStateToProps = ({ user, appReadouts }: IRootState) => {
    return {
        languageId: user.languageId,
        organizationId: user.organizationId,
        supportedLanguages: appReadouts.supportedLanguages,
    };
};

type ReduxType = IFooterTranslateProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatcherToProps>;

export default connect(mapStateToProps, mapDispatcherToProps)(FooterTranslate);
